import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { getDomainConfig } from '../../utils/seoConfig';
import { generatePersonStructuredData } from '../../utils/structuredData';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = "Software Engineer Montreal, AI Education Montreal, Full Stack Developer Quebec, React Developer Montreal, TypeScript Developer, DevOps Montreal, STEM Education Quebec, Web Developer Montreal, Software Development Montreal, Achref Samoud",
  image,
  url,
  type = "website"
}) => {
  const config = getDomainConfig();
  
  const seoTitle = title || `Achref Samoud | Software Engineer & AI Education Specialist in Montreal, QC`;
  const seoDescription = description || `Software Engineer & AI Education Specialist in Montreal, Quebec. Full-stack developer with expertise in React, TypeScript, DevOps research, and STEM education. Available for software development projects in Montreal, QC, Canada.`;
  const seoImage = image ? `${config.domain}${image}` : `${config.domain}/images/profile.jpg`;
  const seoUrl = url || config.domain;
  const structuredData = generatePersonStructuredData();
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={keywords} />
      
      {/* Geographic SEO */}
      <meta name="geo.region" content="CA-QC" />
      <meta name="geo.placename" content="Montreal" />
      <meta name="geo.position" content="45.5017;-73.5673" />
      <meta name="ICBM" content="45.5017, -73.5673" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:locale" content="en_CA" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={seoUrl} />
      <meta property="twitter:title" content={seoTitle} />
      <meta property="twitter:description" content={seoDescription} />
      <meta property="twitter:image" content={seoImage} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={seoUrl} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content="Achref Samoud" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="google-site-verification" content="" />
    </Helmet>
  );
};

const SEOProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <HelmetProvider>
      {children}
    </HelmetProvider>
  );
};

export { SEO, SEOProvider };
export default SEO;